// Perfekter EDIFACT Formatter 
import React, { useRef, useState } from "react";
import { LuCircleAlert, LuInfo, LuPlus, LuDownload, LuTrash2 } from "react-icons/lu";
import PrivacyPopup from "../components/PrivacyPopup";
import SeoHead from "../components/SeoHead";
import { pageMeta } from "../data/pageMeta";
import { ToolHero, Dropzone, useResultScroll } from "../components/tools";
import { toTable, writeSegments, readEdifactFile } from "../utils/edifact";

const MAX_BYTES = 5 * 1024 * 1024;
// Große Dateien: nur die ersten Segmente als Eingabefelder anzeigen (der Download enthält immer alles)
const DISPLAY_LIMIT = 2000;

export default function EdifactFormatter() {
  const [tableRows, setTableRows] = useState([]);
  const [separators, setSeparators] = useState(null); // Trennzeichen der Datei (UNA)
  const [error, setError] = useState("");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const fileInputRef = useRef();
  const [loadedFile, setLoadedFile] = useState(null); // neue Datei → zum Ergebnis scrollen
  const resultRef = useResultScroll(loadedFile);

  const loadToken = useRef(0); // verhindert, dass ein älterer Ladevorgang ein neueres Ergebnis überschreibt

  const processFile = async (file) => {
    if (!file) return;
    const token = ++loadToken.current;

    // Größe, Zeichensatz (UTF-8 oder Latin-1) und EDIFACT-Struktur prüfen
    const { text, error } = await readEdifactFile(file, MAX_BYTES);
    if (token !== loadToken.current) return;
    if (error) {
      setError(error);
      setTableRows([]);
      return;
    }

    try {
      // Rohwerte behalten (inkl. Freigabezeichen), damit die Datei unverändert zurückgeschrieben werden kann
      const { rows, sep } = toTable(text, { raw: true });
      setTableRows(rows);
      setSeparators(sep);
      setError("");
      setLoadedFile({ name: file.name });
    } catch (err) {
      console.error('File processing error:', err);
      setError("❌ Fehler beim Verarbeiten der Datei.");
      setTableRows([]);
    }
  };

  const handleFile = (file) => {
    if (!file) return;
    
    // Prüfen ob Datenschutz bestätigt wurde
    if (!privacyAccepted) {
      setPendingFile(file);
      setShowPrivacyPopup(true);
      return;
    }
    
    // Datenschutz ist akzeptiert, Datei direkt verarbeiten
    processFile(file);
  };

  const handlePrivacyAccept = () => {
    setPrivacyAccepted(true);
    setShowPrivacyPopup(false);
    // Verarbeite die wartende Datei
    if (pendingFile) {
      processFile(pendingFile);
      setPendingFile(null);
    }
  };

  const handleCellEdit = (i, j, value) => {
    const updated = tableRows.map(row => [...row]);
    updated[i][j] = value;
    setTableRows(updated);
  };

  const handleRowDelete = (i) => {
    const updated = [...tableRows];
    updated.splice(i, 1);
    setTableRows(updated);
  };

  const handleAddRow = () => {
    if (!tableRows[0]) return;
    const empty = Array(tableRows[0].length).fill("");
    // Bei gekürzter Anzeige an der letzten sichtbaren Stelle einfügen, sonst am Ende
    if (tableRows.length - 1 > DISPLAY_LIMIT) {
      setTableRows([...tableRows.slice(0, DISPLAY_LIMIT + 1), empty, ...tableRows.slice(DISPLAY_LIMIT + 1)]);
    } else {
      setTableRows([...tableRows, empty]);
    }
  };

  const handleDownload = () => {
    const formattedContent = writeSegments(tableRows.slice(1), separators);
    const blob = new Blob([formattedContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "edifact_formatted.edi";
    a.click();
    URL.revokeObjectURL(url);
  };

  const isTruncated = tableRows.length - 1 > DISPLAY_LIMIT;

  return (
    <>
      <SeoHead
        {...pageMeta["/edifact-formatter"]}
        keywords="EDIFACT Formatter, EDI Formatter, EDIFACT formatieren, EDI Code Formatter, EDIFACT Struktur, EDI Beautifier"
        ogUrl="https://edi-converter.de/edifact-formatter"
      />

      <ToolHero
        tool="format"
        title="EDIFACT Formatter"
        description="EDIFACT-Dateien professionell formatieren – für saubere Struktur und bessere Lesbarkeit."
        intro="EDIFACT-Datei hier ablegen und sofort mit der Formatierung starten."
      >
        <Dropzone
          onFile={handleFile}
          inputRef={fileInputRef}
          hint="Unterstützte Formate: .edi, .txt (max. 5 MB)"
          loadedName={tableRows.length > 0 ? loadedFile?.name : ""}
        />

        {error && (
          <div className="alert alert-danger" role="alert" style={{ marginTop: 16 }}>
            <LuCircleAlert size={20} aria-hidden="true" />
            <span>{error.replace(/^❌\s*/, "")}</span>
          </div>
        )}
      </ToolHero>

      {/* Results Section */}
      {tableRows.length > 0 && (
        <section className="result-section" style={{ paddingTop: 48 }}>
          <div className="container" ref={resultRef}>
            <div className="card">
              <div className="card-header">
                <div>
                  <h2 className="h3">Code-Formatierung &amp; Strukturoptimierung</h2>
                  <p className="small muted">Klicke in eine Zelle, um sie zu bearbeiten. Ein Segment pro Zeile.</p>
                </div>
                <span className="badge badge-neutral">{(tableRows.length - 1).toLocaleString("de-DE")} Segmente</span>
              </div>

              <div className="card-body stack-lg">
                {isTruncated && (
                  <div className="alert alert-info" role="status">
                    <LuInfo size={20} aria-hidden="true" />
                    <span>
                      Große Datei: Bearbeitbar sind die ersten {DISPLAY_LIMIT.toLocaleString("de-DE")} von{" "}
                      {(tableRows.length - 1).toLocaleString("de-DE")} Segmenten. Der Download enthält immer die komplette Datei.
                    </span>
                  </div>
                )}
                <div className="table-wrap table-wrap-tall">
                  <table className="data-table data-table-edit">
                    <thead>
                      <tr>
                        {tableRows[0].map((c, j) => <th key={j}>{c}</th>)}
                        <th aria-label="Aktionen" />
                      </tr>
                    </thead>
                    <tbody>
                      {tableRows.slice(1, DISPLAY_LIMIT + 2).map((r, k) => {
                        const i = k + 1;
                        return (
                          <tr key={i}>
                            {r.map((c, j) => (
                              <td key={j}>
                                <input
                                  type="text"
                                  value={c}
                                  onChange={e => handleCellEdit(i, j, e.target.value)}
                                  readOnly={r[0] === "UNA"} // Trennzeichen-Definition bleibt unverändert
                                  className={"cell-input" + (j === 0 ? " cell-input-seg" : "")}
                                  style={{ width: `calc(${Math.max(String(c).length, 4) + 1}ch + 22px)` }}
                                  aria-label={`${tableRows[0][j]} in Zeile ${i}`}
                                  spellCheck={false}
                                />
                              </td>
                            ))}
                            <td>
                              <button
                                type="button"
                                className="btn btn-danger-ghost btn-icon"
                                onClick={() => handleRowDelete(i)}
                                aria-label={`Zeile ${i} löschen`}
                                title="Zeile löschen"
                              >
                                <LuTrash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="row" style={{ justifyContent: "flex-end" }}>
                  <button type="button" className="btn btn-secondary" onClick={handleAddRow}>
                    <LuPlus size={16} aria-hidden="true" /> Segment hinzufügen
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleDownload}>
                    <LuDownload size={16} aria-hidden="true" /> Formatierte EDI-Datei herunterladen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Privacy Popup */}
      <PrivacyPopup
        show={showPrivacyPopup}
        onAccept={handlePrivacyAccept}
        onCancel={() => setShowPrivacyPopup(false)}
      />
    </>
  );
}
